import React, { useState, useEffect, useCallback } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import PageHeader from "components/pageheader/PageHeader";
import { DefaultAndAuthProps } from "model/props";
import { getLanguageString as _ } from "misc/lang";
import {
  apiGetAccountList,
  apiGetHrGoalsStats,
  apiGetHrRoutinesStats,
  apiGetHrStatsOverview,
} from "net/requests";
import { Organisation } from "model/organisation";
import css from "./hrstatistics.module.scss";
import HrStatsGraphs, { HrGraphData } from "components/stats/HrStatsGraphs";

interface HrOverviewStats {
  account_uuid?: string;
  account_name?: string;
  total_employees?: number;
  active_this_period?: number;
  avg_goal_completion_rate?: number;
  avg_routine_completion_rate?: number;
  total_coins_awarded?: number;
}

const HrStatisticsScreen: React.FC<DefaultAndAuthProps> = (props) => {
  const { admin, account, setAccount, globalAppMode } = props;
  const mode = admin?.is_super_admin
    ? globalAppMode
    : account?.type?.toUpperCase();

  const [organizations, setOrganizations] = useState<Organisation[]>([]);
  const [goals, setGoals] = useState<any[]>([]);
  const [routines, setRoutines] = useState<any[]>([]);
  const [period, setPeriod] = useState("all");
  const [selectedEmployeeUuid, setSelectedEmployeeUuid] = useState<
    string | null
  >(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [overview, setOverview] = useState<HrOverviewStats | null>(null);
  const [orgLoading, setOrgLoading] = useState(false);
  const [orgError, setOrgError] = useState<string>();
  const [activeTab, setActiveTab] = useState(0);

  console.log("goals are", goals);
  console.log("routines are", routines);

  const loadOrganizations = useCallback(() => {
    if (!admin || !admin.is_super_admin || mode !== "HR") return;

    setOrgLoading(true);
    setOrgError(undefined);

    apiGetAccountList(admin, mode)
      .then((response) => {
        const orgs: Organisation[] = response.data;
        setOrganizations(orgs.filter((org) => !org.parent_uuid));
      })
      .catch(() => {
        setOrgError(_("stats", "error_loading_organizations"));
      })
      .finally(() => {
        setOrgLoading(false);
      });
  }, [admin, mode]);

  const loadStats = useCallback(() => {
    if (!admin || mode !== "HR" || !account?.uuid) {
      setGoals([]);
      setRoutines([]);
      setOverview(null);
      return;
    }

    setLoading(true);
    setError(undefined);

    Promise.all([
      apiGetHrGoalsStats(admin, account.uuid, period),
      apiGetHrRoutinesStats(admin, account.uuid, period),
      apiGetHrStatsOverview(admin, account.uuid, period),
    ])
      .then(([goalsResponse, routinesResponse, overviewResponse]) => {
        const goalsData = goalsResponse.data || {};
        setGoals(
          Array.isArray(goalsData.employee_stats)
            ? goalsData.employee_stats
            : [],
        );

        const routinesData = routinesResponse.data || {};
        setRoutines(
          Array.isArray(routinesData.employee_stats)
            ? routinesData.employee_stats
            : [],
        );

        setOverview(overviewResponse.data || null);
      })
      .catch(() => {
        setError(_("stats", "error_loading_hr_statistics"));
        setGoals([]);
        setRoutines([]);
        setOverview(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [admin, account?.uuid, mode, period]);

  useEffect(() => {
    loadOrganizations();
  }, [loadOrganizations]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const uniqueEmployees = React.useMemo(() => {
    const employeeMap = new Map<string, string>();
    goals.forEach((g) => {
      if (g.employee_uuid)
        employeeMap.set(g.employee_uuid, g.employee_name || g.employee_uuid);
    });
    routines.forEach((r) => {
      if (r.employee_uuid)
        employeeMap.set(r.employee_uuid, r.employee_name || r.employee_uuid);
    });
    return Array.from(employeeMap.entries()).map(([uuid, name]) => ({
      uuid,
      name,
    }));
  }, [goals, routines]);

  useEffect(() => {
    if (uniqueEmployees.length > 0) {
      if (
        !selectedEmployeeUuid ||
        !uniqueEmployees.find((e) => e.uuid === selectedEmployeeUuid)
      ) {
        setSelectedEmployeeUuid(uniqueEmployees[0].uuid);
      }
    } else {
      setSelectedEmployeeUuid(null);
    }
  }, [uniqueEmployees, selectedEmployeeUuid]);

  const renderGoalsGraphs = () => {
    if (!selectedEmployeeUuid) return null;
    const filteredGoals = goals.filter(
      (g) => g.employee_uuid === selectedEmployeeUuid,
    );

    if (!filteredGoals.length) {
      return (
        <div className={css.empty}>{_("stats", "no_statistics_data")}</div>
      );
    }

    const graphData: HrGraphData[] = filteredGoals.map((row) => ({
      label: row.employee_name || row.employee_uuid || "-",
      completionRate:
        Math.round(parseFloat(row.avg_completion_rate) * 100) || 0,
      totalActivities: row.total_routine_dates || 0,
      completedActivities: row.completed_routine_dates || 0,
    }));

    return (
      <HrStatsGraphs
        data={graphData}
        title={_("stats", "hr_goals")}
        activityLabel={_("stats", "total_routine_dates")}
        chartType="doughnut"
      />
    );
  };

  const renderRoutinesGraphs = () => {
    if (!selectedEmployeeUuid) return null;
    const filteredRoutines = routines.filter(
      (r) => r.employee_uuid === selectedEmployeeUuid,
    );

    if (!filteredRoutines.length) {
      return (
        <div className={css.empty}>{_("stats", "no_statistics_data")}</div>
      );
    }

    const graphData: HrGraphData[] = filteredRoutines.map((row) => ({
      label: row.employee_name || row.employee_uuid || "-",
      completionRate: Math.round(parseFloat(row.completion_rate) * 100) || 0,
      totalActivities: row.total_dates || 0,
      completedActivities: row.completed_dates || 0,
    }));

    return (
      <HrStatsGraphs
        data={graphData}
        title={_("stats", "hr_routines")}
        activityLabel={_("stats", "total_dates")}
        chartType="bar"
      />
    );
  };

  const renderTotalGraphs = () => {
    if (!goals.length && !routines.length) return null;

    const totalGoals: HrGraphData = {
      label: _("stats", "hr_goals"),
      completionRate: 0,
      totalActivities: 0,
      completedActivities: 0,
    };

    goals.forEach((row) => {
      totalGoals.totalActivities += row.total_routine_dates || 0;
      totalGoals.completedActivities += row.completed_routine_dates || 0;
    });
    if (totalGoals.totalActivities > 0) {
      totalGoals.completionRate = Math.round(
        (totalGoals.completedActivities / totalGoals.totalActivities) * 100,
      );
    }

    const totalRoutines: HrGraphData = {
      label: _("stats", "hr_routines"),
      completionRate: 0,
      totalActivities: 0,
      completedActivities: 0,
    };

    routines.forEach((row) => {
      totalRoutines.totalActivities += row.total_dates || 0;
      totalRoutines.completedActivities += row.completed_dates || 0;
    });
    if (totalRoutines.totalActivities > 0) {
      totalRoutines.completionRate = Math.round(
        (totalRoutines.completedActivities / totalRoutines.totalActivities) *
          100,
      );
    }

    return (
      <HrStatsGraphs
        data={[totalGoals, totalRoutines]}
        title={_("stats", "overall_summary") || "Overall Summary"}
        activityLabel={_("stats", "activities") || "Activities"}
        showLabels={true}
      />
    );
  };

  const renderOverviewCards = () => {
    if (!overview) return null;

    const items = [
      {
        label: _("stats", "kpi_total_employees") || "Total employees",
        value: overview.total_employees ?? "-",
      },
      {
        label: _("stats", "kpi_active_this_period") || "Active this period",
        value: overview.active_this_period ?? "-",
      },
      {
        label:
          _("stats", "kpi_avg_goal_completion_rate") || "Avg goal completion",
        value:
          overview.avg_goal_completion_rate !== undefined &&
          overview.avg_goal_completion_rate !== null
            ? `${Math.round(overview.avg_goal_completion_rate * 10000) / 100}%`
            : "-",
      },
      {
        label:
          _("stats", "kpi_avg_routine_completion_rate") ||
          "Avg routine completion",
        value:
          overview.avg_routine_completion_rate !== undefined &&
          overview.avg_routine_completion_rate !== null
            ? `${
                Math.round(overview.avg_routine_completion_rate * 10000) / 100
              }%`
            : "-",
      },
      {
        label: _("stats", "kpi_total_coins_awarded") || "Total coins awarded",
        value: overview.total_coins_awarded ?? "-",
      },
    ];

    return (
      <Box className={css.overviewCards} mt={2}>
        {items.map((item) => (
          <Box key={item.label} className={css.overviewCard}>
            <Typography className={css.overviewCardLabel}>
              {item.label}
            </Typography>
            <Typography className={css.overviewCardValue}>
              {item.value}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  const showSelectOrganization = admin?.is_super_admin && mode === "HR";
  const hasActiveAccount = account?.uuid;

  return (
    <div className={css.container}>
      <PageHeader
        title={_("stats", "main_title")}
        subtitle={_("stats", "statistics_subtitle")}
      />

      {showSelectOrganization && (
        <Box className={css.organizationSelectorOuter} mt={2}>
          <Typography className={css.sectionLabel}>
            {_("stats", "select_organization")}
          </Typography>
          <Box className={css.organizationSelector}>
            {orgLoading && <CircularProgress size={20} />}
            {orgError && <Alert severity="error">{orgError}</Alert>}
            {organizations.map((org) => (
              <Button
                key={org.uuid}
                variant={account?.uuid === org.uuid ? "contained" : "outlined"}
                color={account?.uuid === org.uuid ? "primary" : "inherit"}
                className={css.orgButton}
                onClick={() => setAccount(org as any)}
              >
                {org.name}
              </Button>
            ))}
          </Box>
        </Box>
      )}

      {hasActiveAccount && (
        <Box className={css.periodSelectorOuter} mt={2}>
          <Typography className={css.sectionLabel}>
            {_("stats", "select_period") || "Select Period"}
          </Typography>
          <Box className={css.periodSelector}>
            {["all", "year", "month", "week"].map((p) => (
              <Button
                key={p}
                variant={period === p ? "contained" : "outlined"}
                color={period === p ? "primary" : "inherit"}
                className={css.orgButton}
                onClick={() => setPeriod(p)}
              >
                {_("stats", "period_" + p) ||
                  p.charAt(0).toUpperCase() + p.slice(1)}
              </Button>
            ))}
          </Box>
        </Box>
      )}

      {mode === "HR" && !hasActiveAccount && (
        <Alert severity="info" className={css.infoAlert}>
          {_("stats", "select_organization_to_view")}
        </Alert>
      )}

      {hasActiveAccount && (
        <Box mt={3} mb={1}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab label={_("stats", "tab_company")} />
            <Tab label={_("stats", "tab_individual")} />
          </Tabs>
        </Box>
      )}

      {error && <Alert severity="error">{error}</Alert>}

      {loading && (
        <Box className={css.loadingBox}>
          <CircularProgress />
        </Box>
      )}

      {!loading && mode === "HR" && hasActiveAccount && (
        <Box
          mt={2}
          style={{ backgroundColor: "#fff", padding: 16, borderRadius: 8 }}
        >
          {activeTab === 0 && (
            <>
              {overview && renderOverviewCards()}
              <Box className={css.section} mt={4}>
                {renderTotalGraphs()}
              </Box>
            </>
          )}

          {activeTab === 1 && (
            <>
              <Typography variant="h5" sx={{ mt: 2, mb: 2, fontWeight: 700 }}>
                {_("stats", "employee_breakdown") || "Employee Breakdown"}
              </Typography>

              {uniqueEmployees.length > 0 && (
                <Box mb={3} sx={{ maxWidth: 400 }}>
                  <Typography className={css.sectionLabel} sx={{ mb: 3 }}>
                    {_("stats", "select_employee") || "Select Employee"}
                  </Typography>
                  <Autocomplete
                    options={uniqueEmployees}
                    getOptionLabel={(option) => option.name}
                    value={
                      uniqueEmployees.find(
                        (e) => e.uuid === selectedEmployeeUuid,
                      ) || null
                    }
                    onChange={(event, newValue) => {
                      setSelectedEmployeeUuid(newValue ? newValue.uuid : null);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={_("stats", "employee") || "Employee"}
                        variant="outlined"
                        size="small"
                      />
                    )}
                  />
                </Box>
              )}

              <Box className={css.section}>{renderGoalsGraphs()}</Box>

              <Box className={css.section}>{renderRoutinesGraphs()}</Box>
            </>
          )}
        </Box>
      )}
    </div>
  );
};

export default HrStatisticsScreen;
